import { MouseEvent, useMemo, useState } from "react";
import { Box, Button, Link } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomModal from "../modal/modal.component";
import {
  buildEntityDetailRoute,
  EntityReferenceType,
} from "../../../utils/entityRoutes";
import { EntityPreviewPage } from "./entityPreviewPage.component";

const ENTITY_ROUTE_MATCHERS: Record<EntityReferenceType, RegExp> = {
  client: /^\/clients\/([^/]+)(?:\/|$)/,
  contract: /^\/contracts\/([^/]+)(?:\/|$)/,
  equipment: /^\/equipment-section\/equipments\/([^/]+)(?:\/|$)/,
  job: /^\/service\/jobs\/([^/]+)(?:\/|$)/,
  // Two capture groups (partId, orderedPartId) - joined back to "partId/orderedPartId"
  // below to compare against the composite id built by buildOrderedPartReferenceId.
  orderedPart: /^\/service\/stock-status\/([^/]+)\/ordered-parts\/([^/]+)(?:\/|$)/,
};

type EntityReferenceLinkProps = {
  entityType: EntityReferenceType;
  entityId?: string | number | null;
  label?: string | null;
  canPreview?: boolean;
  stopPropagation?: boolean;
  modalTitle?: string;
};

const handleCellClickEvent = (
  event: MouseEvent<HTMLElement>,
  shouldStopPropagation: boolean
) => {
  event.preventDefault();
  if (shouldStopPropagation) {
    event.stopPropagation();
  }
};

export default function EntityReferenceLink({
  entityType,
  entityId,
  label,
  canPreview = true,
  stopPropagation = true,
  modalTitle,
}: EntityReferenceLinkProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [opened, setOpened] = useState(false);

  const displayValue = label?.trim() ? label : "-";
  const route = useMemo(() => {
    if (!entityId) {
      return "";
    }
    return buildEntityDetailRoute(entityType, entityId);
  }, [entityId, entityType]);

  const isCurrentEntityRoute = useMemo(() => {
    if (!entityId) {
      return false;
    }
    const matcher = ENTITY_ROUTE_MATCHERS[entityType];
    const match = location.pathname.match(matcher);

    if (!match) {
      return false;
    }

    // Join all capture groups (e.g. orderedPart's partId + orderedPartId) so
    // both single- and composite-id entity types compare correctly.
    const matchedId = match.slice(1).filter(Boolean).join("/");

    if (!matchedId) {
      return false;
    }

    return matchedId === String(entityId);
  }, [entityId, entityType, location.pathname]);

  if (!canPreview || !entityId || displayValue === "-" || isCurrentEntityRoute) {
    return (
      <Box
        component="span"
        sx={{ fontSize: "0.875rem", lineHeight: 1.4, userSelect: "text" }}
      >
        {displayValue}
      </Box>
    );
  }

  return (
    <>
      <Link
        href={route || undefined}
        underline="hover"
        onClick={(event) => {
          // Let modifier-clicks (open in new tab/window) behave natively.
          if (
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey ||
            event.button !== 0
          ) {
            return;
          }
          handleCellClickEvent(event, stopPropagation);
          // Don't open the preview when the click was the end of a text
          // selection - let the user copy the label instead.
          if (window.getSelection()?.toString()) {
            return;
          }
          setOpened(true);
        }}
        sx={{
          textAlign: "left",
          fontSize: "0.875rem",
          lineHeight: 1.4,
          userSelect: "text",
          cursor: "pointer",
        }}
      >
        {displayValue}
      </Link>
      <CustomModal
        opened={opened}
        setOpened={setOpened}
        width="min(1200px, 96vw)"
        title={modalTitle || t("Preview")}
        headerActions={
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setOpened(false);
              navigate(route);
            }}
          >
            {t("Open full view")}
          </Button>
        }
        content={
          <Box sx={{ width: "100%" }}>
            <EntityPreviewPage entityType={entityType} entityId={entityId} />
          </Box>
        }
      />
    </>
  );
}
